import React, { useContext, useEffect, useMemo } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Table.css";
import Sortdownarrow from "../../IconComponents/Sortdownarrow";
import Sortuparrow from "../../IconComponents/Sortuparrow";
import Sortingarrow from "../../IconComponents/Sortingarrow";
import TableHeader from "./TableHeader/TableHeader";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { ThemeContext } from "../../..";

// Default filter UI for a column
const DefaultColumnFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}) => {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // undefined to remove the filter entirely
      }}
      placeholder={`Filter...`}
      style={{
        width: "100%",
        padding: "5px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "5px",
      }}
    />
  );
};

const CustomTableColdWave = (props) => {
  const {
    columns,
    scroll = false, // Default to false if not provided
    data,
    numberOfRows,
    headerTableHeadStyle = {},
    tableStyle = {},
    showRowCount = true,
    rowCount,
    tabChanger = false,
    activeTab,
    setActiveTab,
    searchData,
    setSearchData = () => {},
    enableColumnFilters = false, // New prop to control filter visibility
    disbalePaginationFilter = false,
    disbalePaginationFilter1 = false,
    disbalePaginationFilter2 = false,
    showCumulativeNote = false,
    resourceTable,
    showTotals = false,
    tableTotals, // Receive totals as prop
    totalsRowStyle = { // Add default totals row style
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
      fontSize: '14px',
      borderTop: '2px solid #000',
      color: '#000'
    }
  } = props;

  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);

  // Get totals row data
  const getTotalsRow = () => {
    if (!showTotals || !tableTotals || Object.keys(tableTotals).length === 0) return null;
    
    const totalsRow = {};
    
    columns.forEach(column => {
      if (column.accessor) {
        if (tableTotals[column.accessor] !== undefined) {
          totalsRow[column.accessor] = tableTotals[column.accessor];
        } else if (column.accessor === 'sno' || column.id === 'sno') {
          totalsRow[column.accessor] = '';
        } else if (column.accessor === 'block_name' || column.id === 'block_name') {
          totalsRow[column.accessor] = 'TOTAL';
        } else if (column.accessor === 'remarks' || column.id === 'remarks') {
          totalsRow[column.accessor] = 'NA';
        } else {
          totalsRow[column.accessor] = '';
        }
      }
    });
    
    return totalsRow;
  };

  // Create data with totals row appended
  const dataWithTotals = useMemo(() => {
    const totalsRow = getTotalsRow();
    if (showTotals && totalsRow) {
      return [...data, totalsRow];
    }
    return data;
  }, [data, showTotals, tableTotals]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data: dataWithTotals, // Use the data with totals
      defaultColumn: { Filter: DefaultColumnFilter }, // Add default filter UI
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters, // Use filters hook
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Check if a row is the totals row
  const isTotalsRow = (row) => {
    return row.original.block_name === 'TOTAL';
  };

  const translateNumber = (number) => {
    const numberString = number.toString();
    return i18n.t(`numbers.${numberString}`, { defaultValue: numberString });
  };
  
  let tempRowCount = globalFilter ? rows.length : rowCount || dataWithTotals.length;
  let tempPageIndex = pageIndex;
  let tempPageSize = pageSize;

  if (tempPageIndex === undefined || tempPageIndex === null) {
    tempPageIndex = 0;
  }
  if (tempRowCount === undefined || tempRowCount === null) {
    tempRowCount = 0;
  }
  if (tempPageSize === undefined || tempPageSize === null) {
    tempPageSize = 10;
  }

  const startIndex = tempPageIndex * tempPageSize + 1 || 1;
  const endIndex =
    Math.min((tempPageIndex + 1) * tempPageSize, tempRowCount) || tempRowCount;

  const translatedStartIndex = startIndex;
  const translatedEndIndex = endIndex;
  const translatedRowCount = tempRowCount;
  
  useEffect(() => {
    setPageSize(numberOfRows);
  }, [numberOfRows]); // Update page size if numberOfRows changes

  useEffect(() => {
    if (globalFilter) {
      const pageData = page?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    } else {
      const pageData = rows?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    }
  }, [globalFilter, rows]);

   // Dynamically set className
   const tableClassName = scroll
   ? "table iccc--table-scroll"
   : "table iccc--table";

  return (
    <div className="tabl_container">
      <TableHeader
        {...props}
        searchValue={globalFilter || ""}
        onSearchHandler={setGlobalFilter}
      />
      {tabChanger && (
        <div className="tabchanger_container">
          <div
            className={`tab ${activeTab === "Affected" ? "active" : ""}`}
            onClick={() => setActiveTab("Affected")}>
            Affected
          </div>
          <div
            className={`tab ${
              activeTab === "Partially Affected" ? "active" : ""
            }`}
            onClick={() => setActiveTab("Partially Affected")}>
            Partially Affected
          </div>
        </div>
      )}

      <div className="iccc__table--container" style={tableStyle}>
        <table {...getTableProps()} className={tableClassName} id="report_table">
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{
                    ...headerTableHeadStyle,
                    ...(resourceTable && {
                      position: 'relative',
                      height: '100px', // or suitable height for filter alignment
                      padding: '8px',
                    }),
                  }}
                  key={index}
                >
                  {resourceTable ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      <div>
                        {column.render("Header")}
                        {!column.disableSortBy &&
                          (column.isSorted ? (
                            column.isSortedDesc ? (
                              <Sortdownarrow />
                            ) : (
                              <Sortuparrow />
                            )
                          ) : (
                            <Sortingarrow />
                          ))}
                      </div>
                
                      {enableColumnFilters && column.canFilter && (
                        <div style={{ marginTop: 'auto' }}>{column.render("Filter")}</div>
                      )}
                    </div>
                  ) : (
                    <>
                      {column.render("Header")}
                      {!column.disableSortBy &&
                        (column.isSorted ? (
                          column.isSortedDesc ? (
                            <Sortdownarrow />
                          ) : (
                            <Sortuparrow />
                          )
                        ) : (
                          <Sortingarrow />
                        ))}
                      {enableColumnFilters && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </>
                  )}
                </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);
                
                // Check if this is the totals row
                const isTotalRow = isTotalsRow(row);
                
                // Apply styling based on whether it's a totals row or regular row
                let rowStyle = {};
                let cellStyle = {};
                
                if (isTotalRow) {
                  // Apply totals row styling
                  rowStyle = {
                    ...totalsRowStyle,
                    backgroundColor: theme === "light" ? '#f0f0f0' : '#2c3e50',
                    color: theme === "light" ? '#000' : '#fff',
                    fontWeight: 'bold',
                    borderTop: '2px solid #000'
                  };
                } else {
                  // Apply existing row styling logic for regular rows
                  const shouldHighlightRow =
                    row.original.affected === 0 &&
                    row.original.partially_affected === 0 &&
                    row.original.not_affected === 0;

                  const areBlocksEqual =
                    row.original.total_block_count ===
                    row.original.totalMarkedBlocks;

                  const isReportNotSubmitted = row.original.report_submitted === 0;
                  const isReportSubmitted = row.original.report_not_submitted === 0;
                  
                  if (isReportSubmitted) {
                    rowStyle.backgroundColor = '#d4edda'; // light green
                  } else if (shouldHighlightRow || isReportNotSubmitted) {
                    rowStyle.backgroundColor = '#f8d7da'; // light red
                  } else if (areBlocksEqual) {
                    rowStyle.backgroundColor = '#d4edda'; // light green
                  } else {
                    rowStyle.backgroundColor = '#fff3cd'; // yellow
                  }
                }

                return (
                  <tr {...row.getRowProps()} key={i} style={rowStyle}>
                    {row.cells.map((cell, i) => {
                      if (isTotalRow) {
                        // Apply special styling for totals row cells
                        const isRemarksColumn = cell.column.accessor === 'remarks';
                        const isSnoColumn = cell.column.accessor === 'sno';
                        const isBlockNameColumn = cell.column.accessor === 'block_name';
                        
                        let textAlign = 'center';

                        
                        
                        if (isRemarksColumn || isBlockNameColumn) {
                          textAlign = 'center';
                        } else if (cell.column.numeric || cell.column.isNumeric) {
                          textAlign = 'center';
                        } else if (isSnoColumn) {
                          textAlign = 'center';
                        } else {
                          textAlign = 'center';
                        }

                        return (
                          <td
                            key={i}
                            {...cell.getCellProps()}
                            style={{
                              fontWeight: 'bold',
                              textAlign: textAlign,
                              borderTop: '2px solid #000',
                              ...(isRemarksColumn && { paddingLeft: '0' })
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      }
                      
                      return (
                        <td
                          key={i}
                          {...cell.getCellProps()}
                          className={"left-align"}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data-text">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <div className="d-flex justify-content-between align-items-center">
        {!disbalePaginationFilter2 && 
          <div className="float-start m-2 mt-2 ms-3">
            {showRowCount && (
              <div className="float-start m-2 mt-1 ms-3 pagecount-style">
                {t("showingentriestable.showing")} {translatedStartIndex}{" "}
                {t("showingentriestable.to")} {translatedEndIndex}{" "}
                {t("showingentriestable.of")} {translatedRowCount}{" "}
                {t("showingentriestable.entries")}
              </div>
            )}
            <div>
              {/* <span>
                Page{''}
                <strong>
                  {pageIndex + 1} of {pageOptions.length}
                </strong>{' '} 
              </span> */}
            </div>
          </div>
        }
        
        {showCumulativeNote && (
          <div className="cumulative-note" style={{ 
            marginLeft: '-1px', 
            marginTop: '5px',
            fontStyle: 'italic',
            fontSize: '12px',
            color: theme === "light" ? '#000' : '#fff'
          }}>
            [Note: The data shown is cumulative, starting from January 1st of the selected year up to the chosen date.]
          </div>
        )}
        
        <div className="pagination">
          {!disbalePaginationFilter && (
            <div className="d-flex">
              <span className="pagination_gotopage">
                Go to page:
                <input
                  type="number"
                  min="1"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    let pageNumber = Number(e.target.value);
                    if (pageNumber < 1 || isNaN(pageNumber)) {
                      pageNumber = 1; // Ensures minimum value of 1
                    }
                    gotoPage(pageNumber - 1);
                  }}
                  className="pagination_gotopage-input"
                />
              </span>
              <span>
                <select
                  className="pagination_select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}>
                  {[10, 50, 100, 500, 1000, 2000].map((pageSize) => (
                    <option key={pageSize} value={pageSize} style={{
                      background: theme === "light" ? "white" : "#252b48",
                      color: theme === "light" ? "#2b6591" : "#ffffff"
                    }}>
                      show {pageSize}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          )}
          
          {!disbalePaginationFilter1 && (
            <>
              <button
                className="pagebtn mx-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}>
                {"<"}
              </button>
              <button className="pagination_page">
                {translateNumber(pageIndex + 1)}{" "}
              </button>
              <button
                className="pagebtn"
                onClick={() => nextPage()}
                disabled={!canNextPage}>
                {">"}
              </button>{" "}
              <span className="right_pageno">
                {t("pagination.page_of", {
                  page: translateNumber(pageIndex + 1),
                  total: translateNumber(pageOptions.length),
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomTableColdWave;